import { TextField } from "@mui/material";
import PropTypes from "prop-types";
import React, { useEffect, useState, useCallback } from "react";
import { geocodeByAddress, getLatLng } from "react-places-autocomplete";

import useService from "../../../../../hooks/useService";
import { MAX_TYPE_PER_PROVIDER, inputStyles } from "../../../../cm/Dashboard/FullPages/constant";
import ApiRequestErrorHandler from "../../../../global/ApiRequestErrorHandler";
import FormField from "../../../../global/FormComponents/field/FormField";
import Modal from "../../../../global/Modal";
import SmartyAutoComplete from "../../../../global/SmartyAutoComplete";
import { autoCompleteTextFieldStyles } from "../../../../styled/autocomplete/styles";
import AutoCompleteDropdown from "../../../../ui library/components/dropdowns/AutoCompleteDropdown";

export default function AddCustomProvider({
  openCustomProv,
  setOpenCustomProv,
  checkForAlternativesWithTypes,
}) {
  const [outOfNetworkProv, setOutOfNetworkProv] = useState("");
  const [outOfNetworkPhone, setOutOfNetworkPhone] = useState("");
  const [provLatLng, getProvLatLng] = useState("");
  const [address, setAddress] = useState("");
  const [provTypeOptions, setProvTypeOptions] = useState([]);
  const [selectedProviderTypes, setSelectedProviderTypes] = useState([]);
  const [searchText, setSearchText] = useState("");

  const providerTypesRepository = useService("repositories/provider-type-repository");

  const fetchProviderTypes = useCallback(
    async (search = "") => {
      if (!openCustomProv) {
        return;
      }

      try {
        const response = await providerTypesRepository.query({ search });
        setProvTypeOptions(response ?? []);
      } catch (error) {
        const errArr = ApiRequestErrorHandler(error);
        console.error("Error fetching provider types", errArr);
      }
    },
    [providerTypesRepository, openCustomProv]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchProviderTypes(searchText);
    }, 700);
    return () => clearTimeout(timeoutId);
  }, [searchText, fetchProviderTypes]);

  const handleInputChange = useCallback((event, value, reason) => {
    switch (reason) {
      case "input":
        setSearchText(value);
        break;
      case "clear":
      case "reset":
        setSearchText("");
        break;
      default:
        break;
    }
  }, []);

  const handleValueChange = useCallback((event, value) => {
    if (value && value.length <= MAX_TYPE_PER_PROVIDER) {
      setSelectedProviderTypes(value);
    }
  }, []);

  const customFilterOptions = useCallback(
    (options) => {
      return options.map((option) => ({
        ...option,
        disabled:
          selectedProviderTypes.length >= MAX_TYPE_PER_PROVIDER &&
          !selectedProviderTypes.some((selected) => selected.id === option.id),
      }));
    },
    [selectedProviderTypes]
  );

  const handleNewProviderSubmit = async () => {
    const providerTypes = selectedProviderTypes.map((option) => option.id);
    const newProvData = {
      name: outOfNetworkProv,
      provider_types: providerTypes,
      address: {
        raw: address.raw,
        latitude: provLatLng.lat,
        longitude: provLatLng.lng,
      },
      phone: outOfNetworkPhone,
      in_network: false,
    };

    await checkForAlternativesWithTypes(providerTypes, newProvData);
  };

  const isFormValid =
    outOfNetworkProv &&
    address?.raw &&
    provLatLng?.lat &&
    provLatLng?.lng &&
    selectedProviderTypes.length > 0 &&
    outOfNetworkPhone;

  useEffect(() => {
    if (address && address.raw) {
      geocodeByAddress(address.raw)
        .then((results) => getLatLng(results[0]))
        .then((latLng) => getProvLatLng(latLng))
        .catch((error) => console.error("Error", error));
    }
  }, [address]);

  useEffect(() => {
    fetchProviderTypes();
  }, [fetchProviderTypes]);

  return (
    <Modal
      open={openCustomProv}
      onClose={() => setOpenCustomProv(false)}
      title={"Add Out of Network Provider"}
      actions={[
        {
          label: "Submit Provider",
          variant: "outlined",
          onClick: handleNewProviderSubmit,
          disabled: !isFormValid,
        },
      ]}
      maxWidth="xs"
    >
      <form id="oon-create-form" className="flex flex-col gap-4">
        <FormField label="Out-of-Network Provider Name" required>
          <TextField
            required
            id="name-text"
            sx={inputStyles}
            value={outOfNetworkProv}
            onChange={(event) => {
              setOutOfNetworkProv(event.target.value);
            }}
          />
        </FormField>
        <FormField label="Address" required>
          <SmartyAutoComplete
            address={address}
            setAddress={setAddress}
            inputRequired
            inputSx={inputStyles}
            hideLabel
            containerClassNames="w-full"
          />
        </FormField>

        <FormField label="Out-of-Network Provider Phone" required>
          <TextField
            id="phone-text"
            sx={inputStyles}
            value={outOfNetworkPhone}
            required
            onChange={(event) => {
              setOutOfNetworkPhone(event.target.value);
            }}
          />
        </FormField>
        <FormField label="Provider Type" required>
          <AutoCompleteDropdown
            data={provTypeOptions.map((type) => ({ id: type.id, name: type.name }))}
            initValue={selectedProviderTypes}
            multiple={true}
            noOptionsText="No options available"
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onValueChange={handleValueChange}
            onInputChange={handleInputChange}
            filterOptions={customFilterOptions}
            textFieldSx={autoCompleteTextFieldStyles}
          />
        </FormField>
      </form>
    </Modal>
  );
}

AddCustomProvider.propTypes = {
  openCustomProv: PropTypes.bool,
  setOpenCustomProv: PropTypes.func,
  checkForAlternativesWithTypes: PropTypes.func,
};
